import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { SiteMapUrl } from 'src/app/core/_enums/sitemap.url';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { CallForTenderList } from 'src/app/core/_models/callForTender';
import { CallForTenderService } from 'src/app/core/_services/call-for-tender.service';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { SpinnerOverlayService } from 'src/app/core/_services/spinner.overlay.service';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmCancelDialogComponent } from '../shared/confirm-cancel-dialog/confirm-cancel-dialog.component';
import { SortEvent, TableColumn, TableRow } from '../shared/table/table.component';
import { LabelI18nService } from 'src/app/core/_services/label-i18n.service';

@Component({
  selector: 'app-call-for-tender-list',
  templateUrl: './call-for-tender-list.component.html',
  styleUrls: ['./call-for-tender-list.component.scss']
})
export class CallForTenderListComponent implements OnInit, OnDestroy {
  columns: TableColumn[] = [];
  allRows: TableRow[] = [];
  displayedRows: TableRow[] = [];
  rowsToShow = 20;
  callForTendersCount = 0;
  projectsCount = 0;

  private cftCreatedSubscription!: Subscription;

  constructor(
    private callForTenderService: CallForTenderService,
    private router: Router,
    private spinnerService: SpinnerOverlayService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private labelI18n: LabelI18nService
  ) {}

  ngOnInit(): void {
    this.columns = [
      { key: 'index', label: '#', cssClass: 'fit-content' },
      { key: 'code', label: 'cft_col_code', cssClass: 'fit-content', align: 'center' },
      { key: 'displayName', label: 'cft_col_name', cssClass: 'expand' },
      { key: 'createDate', label: 'cft_col_creation_date', align: 'center', cssClass: 'fit-content' },
      { key: 'updateDate', label: 'cft_col_last_modified_date', align: 'center', cssClass: 'fit-content' },
      { key: 'solutionsCount', label: 'cft_col_solutions_count', align: 'center', cssClass: 'fit-content' }
    ];
    this.loadInitialData();

    this.cftCreatedSubscription = this.callForTenderService.cftCreated$.subscribe(() => {
      this.loadInitialData();
    });
  }

  ngOnDestroy(): void {
    this.cftCreatedSubscription?.unsubscribe();
  }

  private loadInitialData(): void {
    this.spinnerService.show();
    this.callForTenderService.getAll().subscribe({
      next: (callForTenderList: CallForTenderList) => {
        this.allRows = callForTenderList.rows.map((row, index) => ({
          index: index + 1,
          displayName: row.displayName,
          code: row.code,
          createDate: row.createDate ? new Date(row.createDate).toLocaleDateString(this.labelI18n.locale) : '-',
          updateDate: row.updateDate ? new Date(row.updateDate).toLocaleDateString(this.labelI18n.locale) : '-',
          resourceId: row.id,
          solutionsCount: row.solutionsCount
        }));
        this.callForTendersCount = callForTenderList.callForTenderCount;
        this.projectsCount = callForTenderList.projectCount;
        this.updateDisplayedRows();
        this.spinnerService.hide();
      },
      error: () => {
        this.spinnerService.hide();
      }
    });
  }

  onRowClick(row: TableRow): void {
    if (row.resourceId) {
      this.router.navigate([`${SiteMapUrl.MenuCallForTender}/${row.resourceId as string}`]);
    }
  }

  onCreateClick(): void {
    this.callForTenderService.openCreateModal();
  }

  private updateDisplayedRows(): void {
    this.displayedRows = this.allRows.slice(0, this.rowsToShow);
  }

  showMore(): void {
    this.rowsToShow += 10;
    this.updateDisplayedRows();
  }

  get hasMoreRows(): boolean {
    return this.displayedRows.length < this.allRows.length;
  }

  onSort(event: SortEvent): void {
    this.allRows.sort((a, b) => {
      const aValue = a[event.column];
      const bValue = b[event.column];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      let comparison = 0;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).toLowerCase().localeCompare(String(bValue).toLowerCase());
      }

      return event.direction === 'asc' ? comparison : -comparison;
    });

    this.updateDisplayedRows();
  }

  onDeleteClick(row: TableRow): void {
    const dialogRef = this.dialog.open(ConfirmCancelDialogComponent, {
      data: {
        message: this.translate.instant('cft_confirm_delete_tender', { name: row.displayName }),
        confirmText: this.translate.instant('common_btn_delete'),
        cancelText: this.translate.instant('common_btn_cancel'),
        confirmColor: 'warn'
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed || !row.resourceId) return;
      this.spinnerService.show();
      const deletedId = row.resourceId as string;
      this.callForTenderService.delete(deletedId).subscribe({
        next: (success) => {
          this.spinnerService.hide();
          if (success) {
            this.snackbarService.open(SnackbarTypes.delete);
            this.callForTenderService.cftDeleted$.next([deletedId]);
            this.loadInitialData();
          }
        },
        error: () => {
          this.spinnerService.hide();
        }
      });
    });
  }
}
